/* eslint-disable prettier/prettier */
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import RefreshIcon from '@mui/icons-material/Refresh'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Divider,
    FormControlLabel,
    MenuItem,
    Paper,
    Select,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from '@mui/material'
import PropTypes from 'prop-types'
import React, { useCallback, useEffect, useState } from 'react'

import ErrorBanner from '../../components/shared/ErrorBanner'
import { ReputationService } from '../../services/sendingDomainService'
import {
    ACCENT,
    BRAND,
    ICON,
    RADIUS,
    STATUS,
    SURFACE,
    TEXT,
    menuProps,
    pageSx,
    tint,
} from '../../theme/tokens'

/**
 * Sending reputation.
 *
 * The account complaint rate is the number AWS enforces on, and it is an
 * average. Ours sits around 0.09% -- comfortably under the 0.1% review
 * threshold -- while individual campaigns run 3-9x that. Averages hide
 * exactly the thing you need to fix, so this page shows both and never lets
 * the healthy one stand in for the other.
 *
 * Two rules the rendering follows throughout:
 *
 *  1. A NULL RATE IS UNKNOWN, NOT ZERO. If CloudWatch or the log query could
 *     not be read, this says so. Showing 0.00% for unreadable data tells an
 *     operator they are clean when nobody knows.
 *
 *  2. SAMPLED COUNTS ARE LABELLED. Per-campaign queries cap the rows they
 *     read. A capped result is a floor, not a total, and is marked as such --
 *     otherwise "412 complaints" gets quoted as if it were the full figure.
 *
 * Read-only. Nothing on this page changes sending behaviour.
 */

// Live. The nightly aggregation is deployed and the 7-day window is complete
// (verified in prod: days_present 7/7, 140 campaigns seen, 58 ranked).
//
// The 30-day window fills in as daily partials accumulate -- the endpoint
// reports days_present/days_missing, and this page shows that rather than
// presenting a short window as a full one.

const WINDOWS = [
    { label: 'Last 7 days', value: 7 },
    { label: 'Last 14 days', value: 14 },
    { label: 'Last 30 days', value: 30 },
    { label: 'Last 90 days', value: 90 },
]

const STANDING = {
    ok: { color: STATUS.success.fg, label: 'Healthy' },
    review: { color: STATUS.warning.fg, label: 'At review threshold' },
    pause: { color: STATUS.error.fg, label: 'At pause threshold' },
    unknown: { color: TEXT.faint, label: 'Unknown' },
}

/** Percentages, to a fixed 3dp so small movements stay visible. */
const pct = (v) => (v === null || v === undefined ? '—' : `${v.toFixed(3)}%`)
const num = (v) => (v === null || v === undefined ? '—' : v.toLocaleString())

/**
 * Colour a campaign's complaint rate against AWS's thresholds.
 *
 * The thresholds are account-level, so applying them to a single campaign is a
 * deliberate simplification: a campaign at 0.9% is not itself in breach, but
 * it is the reason the account average climbs. Treating it as a problem at the
 * same line is the useful reading.
 */
const rateColor = (rate, threshold) => {
    if (rate === null || rate === undefined) return TEXT.faint
    if (rate >= threshold * 5) return STATUS.error.fg
    if (rate >= threshold) return STATUS.warning.fg
    return STATUS.success.fg
}

const MetricTile = ({ label, rate, status, thresholds, hint }) => {
    const standing = STANDING[status] || STANDING.unknown
    return (
        <Paper
            elevation={0}
            sx={{
                flex: '1 1 200px',
                minWidth: 200,
                p: 2,
                background: SURFACE.cardRaised,
                border: `1px solid ${tint(standing.color, 0.35)}`,
                borderRadius: `${RADIUS.lg}px`,
            }}
        >
            <Box
                sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}
            >
                <Typography sx={{ typography: 'overline', color: TEXT.muted }}>
                    {label}
                </Typography>
                {hint && (
                    <Tooltip title={hint} arrow>
                        <InfoOutlinedIcon
                            sx={{ fontSize: ICON.sm, color: TEXT.faint }}
                        />
                    </Tooltip>
                )}
            </Box>
            <Typography
                sx={{
                    typography: 'h1',
                    color: standing.color,
                    lineHeight: 1.1,
                }}
            >
                {pct(rate)}
            </Typography>
            <Typography
                sx={{ typography: 'caption', color: TEXT.secondary, mt: 0.5 }}
            >
                {standing.label}
            </Typography>
            {thresholds && (
                <Typography
                    sx={{ typography: 'caption', color: TEXT.faint, mt: 0.75 }}
                >
                    Review at {thresholds.review}% · Pause at {thresholds.pause}
                    %
                </Typography>
            )}
        </Paper>
    )
}

MetricTile.propTypes = {
    label: PropTypes.string.isRequired,
    rate: PropTypes.number,
    status: PropTypes.string,
    thresholds: PropTypes.object,
    hint: PropTypes.string,
}

/**
 * Campaigns the last aggregation looked at. The API reports them as
 * `with_delivery` (rated) plus `low_volume_count` (below the delivered
 * floor); the page read `campaigns_seen` / `examined`, which this route never
 * returns, and showed "undefined campaigns seen".
 */
export const campaignsSeen = (report) =>
    report?.campaigns_seen ??
    report?.examined ??
    (report?.with_delivery ?? 0) + (report?.low_volume_count ?? 0)

const Reputation = () => {
    const [account, setAccount] = useState(null)
    const [accountChecks, setAccountChecks] = useState([])
    const [report, setReport] = useState(null)
    const [days, setDays] = useState(30)
    const [overOnly, setOverOnly] = useState(false)
    const [loadingAccount, setLoadingAccount] = useState(true)
    const [loadingCampaigns, setLoadingCampaigns] = useState(true)
    const [campaignError, setCampaignError] = useState('')
    const [error, setError] = useState('')

    const loadAccount = useCallback(async (refresh = false) => {
        setLoadingAccount(true)
        try {
            const res = await ReputationService.account(refresh)
            const data = res?.data || res
            if (data?.success) {
                setAccount(data.reputation || null)
                setAccountChecks(data.checks || [])
            } else {
                setError(data?.error || 'Could not read account reputation')
            }
        } catch (e) {
            setError(
                e?.response?.data?.error ||
                    e?.message ||
                    'Could not read account reputation'
            )
        } finally {
            setLoadingAccount(false)
        }
    }, [])

    const loadCampaigns = useCallback(async (window, filtered) => {
        setLoadingCampaigns(true)
        setCampaignError('')
        try {
            const res = await ReputationService.campaigns({
                days: window,
                overThresholdOnly: filtered,
            })
            const data = res?.data || res
            if (data?.success) {
                setReport(data)
            } else {
                // Clear any previous result: a stale ranking rendered beside a
                // fresh error reads as current.
                setReport(null)
                setCampaignError(data?.error || 'Could not rank campaigns')
            }
        } catch (e) {
            setReport(null)
            setCampaignError(
                e?.response?.data?.error ||
                    e?.message ||
                    'Could not rank campaigns'
            )
        } finally {
            setLoadingCampaigns(false)
        }
    }, [])

    useEffect(() => {
        loadAccount(false)
    }, [loadAccount])

    useEffect(() => {
        loadCampaigns(days, overOnly)
    }, [days, overOnly, loadCampaigns])

    const threshold = report?.threshold_pct ?? 0.1
    const campaigns = report?.campaigns || []
    // The endpoint distinguishes "no snapshot exists" from "snapshot exists
    // and is empty". Only the first is "not measured".
    const notMeasured = report && report.available === false
    // Coverage of the requested window. A window summed from fewer days than
    // asked for is still useful, but it is not the window the operator
    // selected, so it must not be labelled as though it were.
    const daysPresent = report?.days_present
    const daysMissing = report?.days_missing
    const partialWindow = Boolean(daysMissing)
    const unavailable = account && account.available === false

    return (
        <Box sx={pageSx}>
            <Box sx={{ px: 3, py: 2.5 }}>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        flexWrap: 'wrap',
                        gap: 1.5,
                        mb: 0.5,
                    }}
                >
                    <Box>
                        <Typography
                            sx={{ typography: 'h3', color: TEXT.primary }}
                        >
                            Sending Reputation
                        </Typography>
                        <Typography
                            sx={{
                                typography: 'caption',
                                color: TEXT.muted,
                                mt: 0.25,
                            }}
                        >
                            AWS enforces on the account rate. This page also
                            shows which campaigns are driving it.
                        </Typography>
                    </Box>
                    <Button
                        variant="outlined"
                        startIcon={<RefreshIcon sx={{ fontSize: ICON.md }} />}
                        onClick={() => {
                            setError('')
                            loadAccount(true)
                            loadCampaigns(days, overOnly)
                        }}
                        sx={{
                            color: TEXT.secondary,
                            borderColor: SURFACE.border,
                            textTransform: 'none',
                            typography: 'caption',
                            '&:hover': {
                                borderColor: BRAND.orange,
                                color: ACCENT.text,
                            },
                        }}
                    >
                        Refresh
                    </Button>
                </Box>

                {error && (
                    <Box sx={{ mt: 2 }}>
                        <ErrorBanner message={error} />
                    </Box>
                )}

                {/* ── Account ─────────────────────────────────────────── */}
                <Typography
                    sx={{
                        typography: 'overline',
                        color: TEXT.muted,
                        mt: 3,
                        mb: 1.25,
                    }}
                >
                    Account standing
                </Typography>

                {loadingAccount ? (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.25,
                            py: 2,
                        }}
                    >
                        <CircularProgress
                            size={16}
                            sx={{ color: BRAND.orange }}
                        />
                        <Typography
                            sx={{ typography: 'caption', color: TEXT.muted }}
                        >
                            Reading CloudWatch…
                        </Typography>
                    </Box>
                ) : unavailable ? (
                    <Paper
                        elevation={0}
                        sx={{
                            p: 2,
                            background: STATUS.warning.bg,
                            border: `1px solid ${STATUS.warning.border}`,
                            borderRadius: `${RADIUS.lg}px`,
                        }}
                    >
                        <Box sx={{ display: 'flex', gap: 1.25 }}>
                            <WarningAmberIcon
                                sx={{
                                    fontSize: ICON.lg,
                                    color: STATUS.warning.fg,
                                    mt: 0.1,
                                }}
                            />
                            <Box>
                                <Typography
                                    sx={{
                                        typography: 'body2',
                                        color: TEXT.primary,
                                        fontWeight: 500,
                                    }}
                                >
                                    Account reputation unreadable
                                </Typography>
                                <Typography
                                    sx={{
                                        typography: 'caption',
                                        color: TEXT.secondary,
                                        mt: 0.25,
                                    }}
                                >
                                    CloudWatch returned no data. This is not a
                                    rate of zero — the account&apos;s standing
                                    is currently unknown.
                                </Typography>
                            </Box>
                        </Box>
                    </Paper>
                ) : (
                    <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                        <MetricTile
                            label="Complaint rate"
                            rate={account?.complaint_rate}
                            status={account?.complaint_status}
                            thresholds={account?.thresholds?.complaint}
                            hint="Recipients marking our mail as spam. Enforcement is account-wide: past the pause threshold, OTPs and payment receipts stop too."
                        />
                        <MetricTile
                            label="Bounce rate"
                            rate={account?.bounce_rate}
                            status={account?.bounce_status}
                            thresholds={account?.thresholds?.bounce}
                            hint="Undeliverable addresses. A high rate points at list quality rather than content."
                        />
                    </Box>
                )}

                {accountChecks.filter((c) => c.level !== 'pass').length > 0 && (
                    <Box
                        sx={{
                            mt: 1.5,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 1,
                        }}
                    >
                        {accountChecks
                            .filter((c) => c.level !== 'pass')
                            .map((c) => (
                                <Box
                                    key={c.id}
                                    sx={{
                                        display: 'flex',
                                        gap: 1,
                                        p: 1.25,
                                        borderRadius: `${RADIUS.md}px`,
                                        background: (c.level === 'fail'
                                            ? STATUS.error
                                            : STATUS.warning
                                        ).bg,
                                        border: `1px solid ${
                                            (c.level === 'fail'
                                                ? STATUS.error
                                                : STATUS.warning
                                            ).border
                                        }`,
                                    }}
                                >
                                    <WarningAmberIcon
                                        sx={{
                                            fontSize: ICON.md,
                                            color: (c.level === 'fail'
                                                ? STATUS.error
                                                : STATUS.warning
                                            ).fg,
                                            mt: 0.15,
                                        }}
                                    />
                                    <Typography
                                        sx={{
                                            typography: 'caption',
                                            color: TEXT.secondary,
                                        }}
                                    >
                                        {c.message}
                                    </Typography>
                                </Box>
                            ))}
                    </Box>
                )}

                <Divider sx={{ borderColor: SURFACE.border, my: 3 }} />

                {/* ── Campaigns ───────────────────────────────────────── */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        flexWrap: 'wrap',
                        gap: 1.5,
                        mb: 1.25,
                    }}
                >
                    <Box>
                        <Typography
                            sx={{ typography: 'overline', color: TEXT.muted }}
                        >
                            Campaigns by complaint rate
                        </Typography>
                        {report && !notMeasured && (
                            <Typography
                                sx={{
                                    typography: 'caption',
                                    color: TEXT.faint,
                                    mt: 0.4,
                                }}
                            >
                                {campaignsSeen(report)} campaigns seen ·{' '}
                                {campaigns.length} above the{' '}
                                {report.min_delivered_for_rate ?? 500}-delivered
                                floor ·{' '}
                                <Box
                                    component="span"
                                    sx={{
                                        color:
                                            report.over_threshold > 0
                                                ? STATUS.warning.fg
                                                : TEXT.faint,
                                    }}
                                >
                                    {report.over_threshold} at or over{' '}
                                    {threshold}%
                                </Box>
                                {/*
                                 * Window coverage, stated plainly. Rates summed
                                 * from 7 of 30 days are honest for those 7 days
                                 * but are NOT a 30-day measurement, and the
                                 * selector says "Last 30 days" regardless.
                                 */}
                                {partialWindow && (
                                    <>
                                        {' · '}
                                        <Box
                                            component="span"
                                            sx={{ color: STATUS.warning.fg }}
                                        >
                                            built from {daysPresent} of{' '}
                                            {daysPresent + daysMissing} days
                                        </Box>
                                    </>
                                )}
                                {!partialWindow && daysPresent ? (
                                    <>
                                        {' · '}all {daysPresent} days present
                                    </>
                                ) : null}
                            </Typography>
                        )}
                    </Box>
                    <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}
                    >
                        <FormControlLabel
                            control={
                                <Switch
                                    size="small"
                                    // Named explicitly: the visible label is a
                                    // sibling Typography, so without this the
                                    // input has no accessible name at all.
                                    inputProps={{
                                        'aria-label':
                                            'Show only campaigns over the complaint threshold',
                                    }}
                                    checked={overOnly}
                                    onChange={(e) =>
                                        setOverOnly(e.target.checked)
                                    }
                                    sx={{
                                        '& .Mui-checked': {
                                            color: BRAND.orange,
                                        },
                                        '& .Mui-checked + .MuiSwitch-track': {
                                            backgroundColor: BRAND.orange,
                                        },
                                    }}
                                />
                            }
                            label={
                                <Typography
                                    sx={{
                                        typography: 'caption',
                                        color: TEXT.secondary,
                                    }}
                                >
                                    Over threshold only
                                </Typography>
                            }
                        />
                        <Select
                            size="small"
                            value={days}
                            onChange={(e) => setDays(e.target.value)}
                            MenuProps={menuProps}
                            sx={{
                                minWidth: 140,
                                typography: 'caption',
                                color: TEXT.primary,
                                background: SURFACE.inputBg,
                                '& .MuiOutlinedInput-notchedOutline': {
                                    borderColor: SURFACE.border,
                                },
                                '& .MuiSvgIcon-root': { color: TEXT.muted },
                            }}
                        >
                            {WINDOWS.map((w) => (
                                <MenuItem key={w.value} value={w.value}>
                                    {w.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </Box>
                </Box>

                <Paper
                    elevation={0}
                    sx={{
                        background: SURFACE.card,
                        border: `1px solid ${SURFACE.border}`,
                        borderRadius: `${RADIUS.lg}px`,
                        overflow: 'hidden',
                    }}
                >
                    {loadingCampaigns ? (
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1.25,
                                p: 3,
                            }}
                        >
                            <CircularProgress
                                size={16}
                                sx={{ color: BRAND.orange }}
                            />
                            <Typography
                                sx={{
                                    typography: 'caption',
                                    color: TEXT.muted,
                                }}
                            >
                                Querying send logs — one query per campaign,
                                this takes a moment…
                            </Typography>
                        </Box>
                    ) : notMeasured ? (
                        // available:false means the nightly job has not run
                        // for this window. Distinct from "no campaigns
                        // complained" and from an error -- nothing has been
                        // measured, so there is nothing to report either way.
                        <Box sx={{ display: 'flex', gap: 1.25, p: 3 }}>
                            <InfoOutlinedIcon
                                sx={{
                                    fontSize: ICON.lg,
                                    color: TEXT.muted,
                                    mt: 0.2,
                                }}
                            />
                            <Box>
                                <Typography
                                    sx={{
                                        typography: 'body2',
                                        color: TEXT.primary,
                                        fontWeight: 500,
                                    }}
                                >
                                    Not measured yet
                                </Typography>
                                <Typography
                                    sx={{
                                        typography: 'caption',
                                        color: TEXT.secondary,
                                        mt: 0.25,
                                    }}
                                >
                                    {report?.reason ||
                                        'The nightly aggregation has not produced a snapshot for this window yet.'}
                                </Typography>
                            </Box>
                        </Box>
                    ) : campaignError ? (
                        // NOT the empty state. A failed query means nothing was
                        // measured, and "no campaigns sent" would be a
                        // fabricated all-clear -- the precise failure this page
                        // exists to avoid.
                        <Box sx={{ display: 'flex', gap: 1.25, p: 3 }}>
                            <WarningAmberIcon
                                sx={{
                                    fontSize: ICON.lg,
                                    color: STATUS.error.fg,
                                    mt: 0.1,
                                }}
                            />
                            <Box>
                                <Typography
                                    sx={{
                                        typography: 'body2',
                                        color: TEXT.primary,
                                        fontWeight: 500,
                                    }}
                                >
                                    Campaign ranking unavailable
                                </Typography>
                                <Typography
                                    sx={{
                                        typography: 'caption',
                                        color: TEXT.secondary,
                                        mt: 0.25,
                                    }}
                                >
                                    {campaignError}. No campaign rates were
                                    measured — this is not a sign that
                                    complaints are low.
                                </Typography>
                            </Box>
                        </Box>
                    ) : campaigns.length === 0 ? (
                        <Box sx={{ p: 3 }}>
                            <Typography
                                sx={{
                                    typography: 'body2',
                                    color: TEXT.secondary,
                                }}
                            >
                                {overOnly
                                    ? `No campaign is at or over ${threshold}% in this window.`
                                    : 'No campaigns delivered mail in this window.'}
                            </Typography>
                            <Typography
                                sx={{
                                    typography: 'caption',
                                    color: TEXT.faint,
                                    mt: 0.5,
                                }}
                            >
                                Campaigns that sent nothing are omitted rather
                                than listed at 0% — no send means no rate.
                            </Typography>
                        </Box>
                    ) : (
                        <Box sx={{ overflowX: 'auto' }}>
                            <Table size="small" sx={{ minWidth: 720 }}>
                                <TableHead>
                                    <TableRow>
                                        {[
                                            'Campaign',
                                            'Delivered',
                                            'Complaints',
                                            'Complaint rate',
                                            'Bounces',
                                            'Bounce rate',
                                        ].map((h, i) => (
                                            <TableCell
                                                key={h}
                                                align={
                                                    i === 0 ? 'left' : 'right'
                                                }
                                                sx={{
                                                    typography: 'overline',
                                                    color: TEXT.muted,
                                                    borderColor: SURFACE.border,
                                                    whiteSpace: 'nowrap',
                                                }}
                                            >
                                                {h}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {campaigns.map((c) => (
                                        <TableRow
                                            key={c.event_type}
                                            sx={{
                                                '&:hover': {
                                                    background:
                                                        SURFACE.cardHover,
                                                },
                                            }}
                                        >
                                            <TableCell
                                                sx={{
                                                    borderColor: SURFACE.border,
                                                    color: TEXT.primary,
                                                    typography: 'caption',
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 0.75,
                                                        flexWrap: 'wrap',
                                                    }}
                                                >
                                                    {c.event_type}
                                                    {c.sampled && (
                                                        <Tooltip
                                                            arrow
                                                            title="Row cap reached: these counts are a floor, not a total. The rate is still representative; the absolute numbers are understated."
                                                        >
                                                            <Chip
                                                                label="sampled"
                                                                size="small"
                                                                sx={{
                                                                    height: 20,
                                                                    typography:
                                                                        'caption',
                                                                    color: STATUS
                                                                        .neutral
                                                                        .fg,
                                                                    background:
                                                                        STATUS
                                                                            .neutral
                                                                            .bg,
                                                                    border: `1px solid ${SURFACE.border}`,
                                                                }}
                                                            />
                                                        </Tooltip>
                                                    )}
                                                </Box>
                                            </TableCell>
                                            <TableCell
                                                align="right"
                                                sx={{
                                                    borderColor: SURFACE.border,
                                                    color: TEXT.secondary,
                                                    typography: 'caption',
                                                }}
                                            >
                                                {num(c.delivered)}
                                            </TableCell>
                                            <TableCell
                                                align="right"
                                                sx={{
                                                    borderColor: SURFACE.border,
                                                    color: TEXT.secondary,
                                                    typography: 'caption',
                                                }}
                                            >
                                                {num(c.complaints)}
                                            </TableCell>
                                            <TableCell
                                                align="right"
                                                sx={{
                                                    borderColor: SURFACE.border,
                                                    typography: 'body2',
                                                    fontWeight: 600,
                                                    color: rateColor(
                                                        c.complaint_rate,
                                                        threshold
                                                    ),
                                                }}
                                            >
                                                {pct(c.complaint_rate)}
                                            </TableCell>
                                            <TableCell
                                                align="right"
                                                sx={{
                                                    borderColor: SURFACE.border,
                                                    color: TEXT.secondary,
                                                    typography: 'caption',
                                                }}
                                            >
                                                {num(c.bounces)}
                                            </TableCell>
                                            <TableCell
                                                align="right"
                                                sx={{
                                                    borderColor: SURFACE.border,
                                                    typography: 'body2',
                                                    color: rateColor(
                                                        c.bounce_rate,
                                                        5
                                                    ),
                                                }}
                                            >
                                                {pct(c.bounce_rate)}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </Box>
                    )}
                </Paper>

                <Typography
                    sx={{
                        typography: 'caption',
                        color: TEXT.faint,
                        mt: 1.5,
                        maxWidth: 720,
                    }}
                >
                    Per-campaign rates are measured against delivered mail,
                    matching how AWS computes the account rate. The {threshold}%
                    line is an account threshold — a single campaign above it is
                    not itself in breach, but it is what pulls the account
                    average up as volume grows.
                </Typography>
            </Box>
        </Box>
    )
}

export default Reputation
